import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  useTheme,
  InputAdornment,
  IconButton,
  Avatar,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const Login = () => {
  const firstInputRef = React.useRef(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, verifyOtp, otpSent } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    if (otpSent && otpCode.every(digit => digit !== '')) {
      handleSubmit();
    }
  }, [otpCode, otpSent]);

  useEffect(() => {
    if (otpSent && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [otpSent]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'username') {
      setUsername(value);
    } else if (name === 'password') {
      setPassword(value);
    } else if (name.startsWith('otpCode')) {
      const index = parseInt(name.slice(-1));
      if (index >= 0 && index < 6) {
        const newOtpCode = [...otpCode];
        newOtpCode[index] = value;
        setOtpCode(newOtpCode);

        // Move focus to next input
        if (value && index < 5) {
          const nextInput = document.querySelector(`input[name=otpCode${index + 1}]`);
          if (nextInput) nextInput.focus();
        }
      }
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setError('');
      setLoading(true);

      if (otpSent) {
        // Verify OTP
        await verifyOtp(otpCode.join(''));
        navigate('/');
      } else {
        // Initial login
        await login(username, password);
      }
    } catch (error) {
      setError(otpSent
        ? 'Invalid OTP code. Please try again.'
        : 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme => theme.palette.mode === 'dark'
          ? theme.palette.background.default
          : '#F8FAFC',
        backgroundImage: theme => theme.palette.mode === 'dark'
          ? 'linear-gradient(45deg, rgba(0, 0, 0, 0.2) 30%, rgba(0, 0, 0, 0.1) 90%)'
          : 'none',
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={theme => theme.palette.mode === 'dark' ? 0 : 6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '16px',
            backgroundColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.03)'
              : '#FFFFFF',
            backdropFilter: theme => theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
            border: theme => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: theme => theme.palette.mode === 'dark'
              ? 'none'
              : '0 8px 32px -4px rgba(0, 0, 0, 0.1), 0 4px 16px -2px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main', mb: 4 }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
            Sign in
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                width: '100%',
                mb: 2,
                borderRadius: '10px',
              }}
            >
              {error}
            </Alert>
          )}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: '100%',
              mt: 1,
            }}
          >
            {otpSent ? (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Please enter the verification code sent to your device
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center',
                    my: 2
                  }}
                >
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <TextField
                      key={index}
                      margin="none"
                      required
                      inputRef={index === 0 ? firstInputRef : null}
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                      sx={{
                        width: '48px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.03)'
                            : '#F8FAFC',
                          '&:hover': {
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#F1F5F9',
                          },
                          '&.Mui-focused': {
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#F1F5F9',
                          },
                        },
                      }}
                      name={`otpCode${index}`}
                      value={otpCode[index]}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleChange({ target: { name: `otpCode${index}`, value } });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
                          const prevInput = document.querySelector(`input[name=otpCode${index - 1}]`);
                          if (prevInput) {
                            prevInput.focus();
                            const newOtpCode = [...otpCode];
                            newOtpCode[index - 1] = '';
                            setOtpCode(newOtpCode);
                          }
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasteData = e.clipboardData.getData('text/plain').replace(/[^0-9]/g, '').slice(0, 6);
                        const newOtpCode = [...otpCode];
                        for (let i = 0; i < pasteData.length; i++) {
                          if (index + i < 6) {
                            newOtpCode[index + i] = pasteData[i];
                          }
                        }
                        setOtpCode(newOtpCode);
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.03)'
                        : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#F1F5F9',
                      },
                      '&.Mui-focused': {
                        backgroundColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#F1F5F9',
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.03)'
                        : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#F1F5F9',
                      },
                      '&.Mui-focused': {
                        backgroundColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#F1F5F9',
                      },
                    },
                  }}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '10px',
                backgroundColor: theme => theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme => theme.palette.primary.dark,
                },
              }}
            >
              {loading ? 'Please wait...' : (otpSent ? 'Verify Code' : 'Sign In')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
